import type {
  ISidebarNavGroup,
  ISidebarNavGroupDef,
  TPerVariant,
  TSidebarNavGroupKey,
  TSidebarVariant,
} from './types';

/** Every variant, for callers that need to span both (e.g. URL matching). */
export const SIDEBAR_VARIANTS: readonly TSidebarVariant[] = [
  'finance',
  'regular',
];

/** Section heading i18n keys (namespace: `navigation`), by group. */
export const SIDEBAR_NAV_GROUP_TITLE_KEYS: Record<TSidebarNavGroupKey, string> =
  {
    workspace: 'navigation.sidebar.groups.workspace',
    configure: 'navigation.sidebar.groups.configure',
    'reason-and-trust': 'navigation.sidebar.groups.reasonAndTrust',
  };

const forVariant = <T extends string>(
  value: TPerVariant<T>,
  variant: TSidebarVariant,
): T => (typeof value === 'string' ? value : value[variant]);

const FINANCE_ONLY: readonly TSidebarVariant[] = ['finance'];
const REGULAR_ONLY: readonly TSidebarVariant[] = ['regular'];

const NAV_GROUPS: ISidebarNavGroupDef[] = [
  {
    key: 'workspace',
    items: [
      {
        key: 'cockpit',
        icon: 'LayoutGrid',
        titleKey: 'finance.sidebar.nav.cockpit',
        ns: 'finance',
        routeKey: 'FINANCE_COCKPIT',
        variants: FINANCE_ONLY,
      },
      {
        key: 'drafts',
        icon: 'PenLine',
        titleKey: 'workspace.drafts.title',
        ns: 'workspace',
        routeKey: 'DRAFTS',
      },
      {
        key: 'automations',
        icon: 'Workflow',
        titleKey: 'automations.title',
        ns: 'automations',
        routeKey: 'AUTOMATIONS',
      },
      // Behind `Consistent_Layout`, one entry replaces drafts and automations:
      // the catalog at `/drafts` lists both, and a live automation's pages sit
      // under `/automations`.
      {
        key: 'automations-catalog',
        icon: 'Workflow',
        titleKey: 'common.automations',
        ns: 'common',
        routeKey: 'DRAFTS',
        activeUnder: ['AUTOMATIONS'],
      },
      {
        key: 'agents',
        icon: 'Blocks',
        titleKey: 'agentBuilder.dashboard.title',
        ns: 'agent-builder',
        routeKey: 'AGENTS',
      },
      {
        key: 'apps',
        icon: 'LayoutGrid',
        titleKey: 'agentApps.nav',
        ns: 'agent-builder',
        routeKey: 'APPS',
        activeUnder: ['APP_DETAIL'],
      },
      {
        key: 'context-graphs',
        icon: 'Network',
        titleKey: 'workspace.nav',
        ns: 'cartographer',
        routeKey: 'CARTOGRAPHER',
      },
      {
        key: 'context-graph',
        icon: 'Network',
        titleKey: 'finance.sidebar.nav.contextGraph',
        ns: 'finance',
        routeKey: 'FINANCE_CONTEXT_GRAPH',
        variants: FINANCE_ONLY,
      },
      {
        key: 'guidance-center',
        icon: 'Sparkle',
        titleKey: 'guidance.center.title',
        ns: 'guidance',
        routeKey: 'GUIDANCE_CENTER',
        variants: REGULAR_ONLY,
      },
      {
        key: 'workbench',
        icon: 'List',
        titleKey: 'workbench.nav',
        ns: 'workbench',
        routeKey: 'WORKBENCH',
      },
    ],
  },
  {
    key: 'configure',
    items: [
      {
        key: 'ingest',
        icon: 'DatabaseZap',
        titleKey: 'finance.nav.ingest',
        ns: 'finance',
        routeKey: 'FINANCE_CONNECT',
        variants: FINANCE_ONLY,
      },
      {
        key: 'integrations',
        icon: 'Plug',
        titleKey: 'integrations.title',
        ns: 'integrations',
        routeKey: 'INTEGRATIONS',
      },
      {
        key: 'detectors',
        icon: 'ScanSearch',
        titleKey: 'finance.nav.detectors',
        ns: 'finance',
        routeKey: 'FINANCE_DETECTORS',
        variants: FINANCE_ONLY,
      },
      {
        key: 'library',
        icon: 'FolderOpen',
        titleKey: 'library.title',
        ns: 'library',
        routeKey: 'LIBRARY',
      },
    ],
  },
  {
    key: 'reason-and-trust',
    items: [
      {
        key: 'findings',
        icon: 'TriangleAlert',
        titleKey: 'finance.nav.findings',
        ns: 'finance',
        routeKey: 'FINANCE_FINDINGS',
        variants: FINANCE_ONLY,
      },
      {
        key: 'sop-vault',
        icon: 'Archive',
        titleKey: 'finance.nav.sopVault',
        ns: 'finance',
        routeKey: 'FINANCE_SOP_VAULT',
        variants: FINANCE_ONLY,
      },
      {
        key: 'learnings',
        icon: 'BookOpen',
        titleKey: 'finance.nav.learnings',
        ns: 'finance',
        routeKey: 'FINANCE_LEARNINGS',
        variants: FINANCE_ONLY,
      },
      {
        key: 'evidence-vault',
        icon: 'FileText',
        titleKey: 'finance.nav.evidence',
        ns: 'finance',
        routeKey: 'FINANCE_EVIDENCE',
        variants: FINANCE_ONLY,
      },
    ],
  },
];

/**
 * The sidebar sections and entries for one variant, in render order. Groups
 * whose every entry is scoped to the other variant are dropped, so a variant
 * never renders a bare heading.
 *
 * @param variant - Which workspace's sidebar to build.
 * @returns The visible groups, each with its resolved entries.
 */
export const getSidebarNavGroups = (
  variant: TSidebarVariant,
): ISidebarNavGroup[] =>
  NAV_GROUPS.map(group => ({
    key: group.key,
    titleKey: SIDEBAR_NAV_GROUP_TITLE_KEYS[group.key],
    items: group.items
      .filter(item => (item.variants ?? SIDEBAR_VARIANTS).includes(variant))
      .map(item => ({
        key: item.key,
        icon: forVariant(item.icon, variant),
        titleKey: forVariant(item.titleKey, variant),
        ns: forVariant(item.ns, variant),
        routeKey: forVariant(item.routeKey, variant),
        activeUnder: item.activeUnder,
      })),
  })).filter(group => group.items.length > 0);
